import type { ConsumerContractRegistryManifest, DomainOwner } from './consumerContractRegistry.types';
import { sub, summarySubscriptionsForArtifact } from './consumerContractRegistry.buildSubscriptions';
import {
  DESIGNER_SUMMARY_FIELD_DEFINITIONS,
  GET_DESIGNER_SUMMARY_ROWS_REF,
} from './consumerContractRegistry.summaryFields';

/** [fieldKey, subscribedField, sourcePath, outputLabel, transformationRule, fallbackBehaviour] */
type OwnFormatterRow = [string, string, string, string, string, string];

function domainOwnerFor(fieldKey: string): DomainOwner {
  return DESIGNER_SUMMARY_FIELD_DEFINITIONS.find((f) => f.fieldKey === fieldKey)?.domainOwner ?? 'quote';
}

const SUPPLIER_PDF_FIELD_ROWS: OwnFormatterRow[] = [
  ['reference', 'Reference', 'meta_json.specs.reference', 'REFERENCE', 'Plain text in a highlighted cell', 'Window N when blank'],
  ['astragal_bars', 'Astragal bars', 'basket astragalOn / windowGeneratorPayload.presentation aBarOn', 'ASTRAGAL', 'formatAstragal reads the payload presentation, not specs.options.astragal_bars', 'Printed blank'],
  ['astragal_bar_side', 'Astragal bar side', 'meta_json.specs.options.astragal_bar_side_label / astragal_bar_side', 'ASTRAGAL BAR', 'Label, else none/inside/internal/outside/external/both → None/Inside/Outside/Both (no astragal_side fallback)', 'None (default)'],
  ['quantity', 'Quantity', 'document_line.qty', 'QUANTITY', 'Number', '1'],
  ['width_height_size', 'Size', 'meta_json.specs.width_mm, height_mm (bay: tangent width)', 'SIZE', 'W{width} x H{height}; bay lines print H{height} only', 'Printed blank'],
  ['bay_tangent_width', 'Bay tangent width', 'meta_json.specs.options.bay_tangent_width_mm or payload geometry', 'TANGENT WIDTH', '{n}mm, bay lines only', 'Row omitted'],
  ['bay_projection_depth', 'Bay projection depth', 'meta_json.specs.options.bay_projection_depth_mm (curved bays)', 'PROJECTION DEPTH', '{n}mm, curved bays only', 'Row omitted'],
  ['bay_joint_angles', 'Bay joint angles', 'meta_json.specs.options.bay_joint_angles_deg or payload geometry', 'JOINT ANGLES (DEG)', 'Rounded degrees joined with commas', 'Row omitted'],
  ['bay_facet_widths', 'Bay facet widths', 'meta_json.specs.options.bay_facet_widths_mm or payload geometry', 'FACET WIDTHS (MM)', 'Rounded mm joined with commas', 'Row omitted'],
  ['notes', 'Notes', 'meta_json.specs.notes', 'NOTES', 'As stored', 'Row omitted when blank'],
];

const SUPPLIER_EMAIL_FIELD_ROWS: OwnFormatterRow[] = [
  ['window_type_id', 'Window type', 'basket windowType', '{{windowType}}', 'Same basket type mapping as supplier PDF', 'casement'],
  ['width_height_size', 'Size', 'basket width / height', '{{width}} / {{height}}', 'Numbers in mm; bay width is tangent width', 'Empty'],
  ['quantity', 'Quantity', 'document_line.qty', '{{quantity}}', 'Number', '1'],
  ['glass_type', 'Glass type', 'basket glassType', '{{glassType}}', 'Same basket glass mapping', 'Clear'],
  ['frame_color_combined', 'Frame colour (shared value)', 'meta_json.specs.frame_color', '{{frameColor}} / {{windowColour}}', 'Shared frame_color only; split colours not exposed', 'White'],
  ['material_name', 'Material', 'basket windowMaterial', '{{windowMaterial}} / {{material}}', 'resolveSingleMaterialName', 'Empty'],
  ['variant', 'Profile', 'basket profileType', '{{profileType}}', 'As stored', 'Empty'],
  ['beading_type', 'Beading type', 'basket beadingType', '{{beadingType}}', 'Same basket beading mapping', 'Empty'],
  ['astragal_bar_side', 'Astragal bar side', 'basket astragalBarSide', '{{astragalBarSide}}', 'Same basket mapping', 'Empty'],
  ['handle_style', 'Handle style', 'basket handleStyle', '{{handleStyle}}', 'Same basket mapping', 'Empty'],
  ['handle_color', 'Handle colour', 'basket handleColor', '{{handleColor}}', 'As stored', 'Empty'],
  ['trickle_vents_parts', 'Trickle vents (yes/no and position)', 'basket trickleVents, trickleVentsPosition', '{{trickleVentsYesNo}} / {{trickleVentsPosition}}', 'Yes/No and position label; {{trickleVents}} uses the shared summary', 'Empty'],
  ['cill_parts', 'Cill parts', 'basket cillIncluded / cillType / cillHeight', '{{cillIncluded}} / {{cillType}} / {{cillHeight}}', 'Separate parts; {{cill}} uses the shared summary', 'Empty'],
  ['notes', 'Notes', 'meta_json.specs.notes', '{{notes}}', 'Visual Designer Notes field only', 'Empty'],
];

const SUPPLIER_BASKET_EXTRA_ROWS: OwnFormatterRow[] = [
  ['reference', 'Reference', 'meta_json.specs.reference', 'WindowBasketItem.reference', 'As stored', 'Window N'],
  ['variant', 'Profile', 'meta_json.specs.variant', 'WindowBasketItem.profileType', 'deriveVariant', 'Empty'],
  ['beading_type', 'Beading type', 'meta_json.specs.beading_type_code / beading_type / variant_code', 'WindowBasketItem.beadingType', 'Code map then keyword match', 'Variant beading type'],
  ['frame_color_combined', 'Frame colour (shared value)', 'meta_json.specs.frame_color', 'WindowBasketItem.frameColor', 'Shared value', 'White'],
  ['frame_color_external', 'External / internal colour names and codes', 'meta_json.specs.frame_color_external / _internal / frame_color_code*', 'frameColorExternalName, frameColorCode* extras', 'Carried on the basket item; convertToWindowOrderItem passes the colours on only inside designerSummaryRows', 'Omitted when blank'],
  ['handle_style', 'Handle style', 'meta_json.specs.options.handle_style / handle_style_code', 'WindowBasketItem.handleStyle', 'Label, else code map', 'Omitted'],
  ['handle_color', 'Handle colour', 'meta_json.specs.options.handle_color', 'WindowBasketItem.handleColor', 'As stored', 'Omitted'],
  ['astragal_bar_side', 'Astragal bar side', 'meta_json.specs.options.astragal_bar_side_label / astragal_bar_side', 'WindowBasketItem.astragalBarSide', 'Label, else code map', 'Omitted'],
  ['trickle_vents', 'Trickle vents', 'meta_json.specs.options.trickle_vents, trickle_vents_position', 'trickleVents, trickleVentsPosition', 'Boolean plus raw position code', 'false'],
  ['notes', 'Notes', 'meta_json.specs.notes', 'WindowBasketItem.notes', 'As stored', 'Omitted'],
];

/** Manually curated Phase 0/1 registry — declared contract, not live quote data. */
export const CONSUMER_CONTRACT_REGISTRY_MANIFEST: ConsumerContractRegistryManifest = {
  schemaVersion: '1.0',
  manifestVersion: '2026.09.29-glass-specification',
  lastReviewedAt: '2026-09-29',
  disclaimer:
    'Declared contract only. This registry documents which fields downstream Visual Designer artifacts consume and how they are mapped. It does not reflect live quote data, canonical payloads, or runtime values.',
  globalWarnings: [
    {
      id: 'door-detection-inconsistent',
      severity: 'drift',
      message:
        'The shared isDoorType (doorTypes.ts: id upvc-door, french-doors, bifold-doors or sliding-doors, OR name contains the word "door") is used by the summary rows, QuoteVisualDesignerCard isDoorTypeId, descriptionTemplates, DashboardPage and the DocumentDetailPage hinge type text. isDoorOnlyTypeId (the same ids, without the name test) drives the DocumentDetailPage and PublicDocViewerPage drawing merges and visualDesignerPayloadResolution, so Door & Window is drawn as a window there. The supplier modal window/door counts and basket type codes list the ids directly, and the fenestration SQL lists the ids plus a word regex (20260929120000-fenestration-new-door-types.sql).',
    },
    {
      id: 'self-formatting-consumers',
      severity: 'duplicate_mapper',
      message:
        'Supplier PDF (reference, size, bay rows, astragal, quantity, notes), supplier email variables other than colours / hinge / opens / hinge type / trickle / cill, the supplier basket projection, the survey clone and the staff PDF flat fallback still read and label fields with their own code.',
    },
  ],
  artifacts: [
    {
      id: 'staff_quote_pdf',
      name: 'Staff quote / estimate PDF',
      audience: 'staff',
      renderSource: 'mixed',
      description:
        'Staff-facing quotation or estimate PDF generated from DocumentDetailPage.renderTemplate via visualDesignerTemplate blocks.',
      entryPoint: 'src/modules/docs/pages/DocumentDetailPage.tsx → renderTemplate → applyVisualDesignerTemplateBlocks',
      warnings: [
        {
          id: 'staff-pdf-fallback-kv',
          severity: 'duplicate_mapper',
          message:
            'visualDesignerTemplate.ts falls back to legacy flat item fields when summaryRows is empty — parallel mapping path to getDesignerSummaryRows.',
        },
        {
          id: 'staff-pdf-flat-open-direction',
          severity: 'drift',
          message:
            'DocumentDetailPage flat item fields map open_direction to In or Out only; Bi-directional becomes blank. Shown only when the legacy flat fallback is used.',
          relatedSubscriptionIds: ['staff_quote_pdf.open-direction-direct-legacy-kv'],
        },
      ],
      subscriptions: [
        ...summarySubscriptionsForArtifact('staff_quote_pdf'),
        sub(
          'staff_quote_pdf',
          'templatePayload',
          'Window Generator canonical payload',
          'canonical_payload',
          'meta_json.window_style.templatePayload',
          'Live WG merge input (when re-rendered)',
          'calculated',
          'Authoritative templatePayload read; compatibility overlay documented separately as wg-payload-merge',
          'Returns raw templatePayload when root/w/h missing',
          'src/modules/docs/services/visualDesignerRenderAssets.service.ts#buildLatestWindowGeneratorPayloadFromMeta',
          { operationPreset: 'canonicalPayloadRead', relationshipPreset: 'canonicalPayloadReference', domainOwner: 'canonical_window' },
        ),
        sub(
          'staff_quote_pdf',
          'preview_path_presentation_external',
          'Presentation outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_presentation_external',
          'Presentation · Outside image slot',
          'exported',
          'Resolved to public URL; preferred over legacy preview_path for v2 layout',
          'Falls back to preview_path_external then preview_path',
          'src/shared/pdf/visualDesignerTemplate.ts#buildVisualDesignerItemsV2Html',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'staff_quote_pdf',
          'preview_path_presentation_internal',
          'Presentation inside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_presentation_internal',
          'Presentation · Inside image slot',
          'exported',
          'Stored PNG URL only in PDF HTML',
          'Omit slot when URL empty',
          'src/shared/pdf/visualDesignerTemplate.ts#buildVisualDesignerItemsV2Html',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'staff_quote_pdf',
          'preview_path_design_external',
          'Design outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_external',
          'Design · Outside image slot',
          'exported',
          'Stored PNG URL in PDF tile grid',
          'Omit slot when URL empty',
          'src/shared/pdf/visualDesignerTemplate.ts#buildVisualDesignerItemsV2Html',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'staff_quote_pdf',
          'preview_path_design_internal',
          'Design inside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_internal',
          'Design · Inside image slot',
          'exported',
          'Stored PNG URL in PDF tile grid',
          'Omit slot when URL empty',
          'src/shared/pdf/visualDesignerTemplate.ts#buildVisualDesignerItemsV2Html',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'staff_quote_pdf',
          'preview_path_bay_schematic',
          'Bay schematic PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_bay_schematic',
          'Bay schematic image slot',
          'conditional',
          'Included only when item detected as bay-like via summary row labels or bay metrics',
          'Omit for non-bay windows',
          'src/shared/pdf/visualDesignerTemplate.ts#isBayLikeItem',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'staff_quote_pdf',
          'width_mm',
          'Overall width (legacy KV fallback)',
          'quote_owned',
          'meta_json.specs.width_mm',
          'Size / legacy KV row',
          'exported',
          'Formatted as "{width} × {height} mm" in legacy fallback table when summaryRows empty',
          'Empty when bay uses tangent width label instead',
          'src/shared/pdf/visualDesignerTemplate.ts#sizeTextFor',
          { idSuffix: 'legacy-kv', operationPreset: 'legacyKvFallback' },
        ),
        sub(
          'staff_quote_pdf',
          'height_mm',
          'Overall height (legacy KV fallback)',
          'quote_owned',
          'meta_json.specs.height_mm',
          'Size / legacy KV row',
          'exported',
          'Paired with width_mm in legacy fallback when summaryRows empty',
          'Empty string omitted from row',
          'src/shared/pdf/visualDesignerTemplate.ts#sizeTextFor',
          { idSuffix: 'legacy-kv', operationPreset: 'legacyKvFallback' },
        ),
        sub(
          'staff_quote_pdf',
          'open_direction',
          'Open direction (legacy KV fallback)',
          'quote_owned',
          'meta_json.specs.options.open_direction',
          'Open direction / legacy KV row',
          'conditional',
          'in → In, out → Out; any other value (including bi-directional) → blank',
          'Row omitted when blank',
          'src/modules/docs/pages/DocumentDetailPage.tsx#renderTemplate visualDesignerItems openDirection',
          { idSuffix: 'legacy-kv', operationPreset: 'legacyKvFallback' },
        ),
        sub(
          'staff_quote_pdf',
          'quantity',
          'Line quantity',
          'quote_owned',
          'document_line.qty',
          'Qty row in KV table',
          'exported',
          'Appended after summary rows in buildKvTableFromSummaryRows',
          'Defaults to empty if missing',
          'src/shared/pdf/visualDesignerTemplate.ts#buildKvTableFromSummaryRows',
          { operationPreset: 'directReadFormat' },
        ),
        sub(
          'staff_quote_pdf',
          'wg-payload-merge',
          'Merged WG payload for export',
          'derived_cache',
          'resolveExportWindowGeneratorPayload(meta) — authoritative templatePayload or legacy specs merge',
          'PNG blob generation input',
          'calculated',
          'Current v2_lite lines: saved templatePayload directly. Legacy: mergeModalStateIntoPayload with specs dimensions',
          'Returns unmerged templatePayload on merge failure',
          'visualDesignerPayloadResolution.ts; visualDesignerRenderAssets.service.ts',
          { operationPreset: 'compatibilityOverlayCalculate', relationshipPreset: 'compatibilityOverlay', domainOwner: 'derived_cache' },
        ),
      ],
    },
    {
      id: 'public_customer_pdf',
      name: 'Public customer PDF',
      audience: 'customer',
      renderSource: 'stored_png',
      description:
        'Customer-facing PDF served by the public-doc Supabase edge function. Line meta passes through sanitizeVisualMeta (shared allow-list in publicVisualMeta.ts; price-like keys are never on the list and a test guards this) before render. Spec rows come from getDesignerSummaryRows run on the allow-listed meta, so wording matches the Quote page. Visual sections use stored PNG URLs. templatePayload is read for bay detection and bay metrics. Omitted keys are documented per-field (fieldOmittedSanitize).',
      entryPoint:
        'supabase/functions/public-doc/index.ts → sanitizeVisualMeta → getDesignerSummaryRows (summaryRows) → applyVisualDesignerTemplateBlocks',
      warnings: [
        {
          id: 'public-pdf-no-live-render',
          severity: 'drift',
          message: 'Public customer PDF does not live-render Window Generator; it relies on stored preview paths only.',
        },
      ],
      subscriptions: [
        ...summarySubscriptionsForArtifact('public_customer_pdf'),
        sub(
          'public_customer_pdf',
          'templatePayload',
          'Window Generator payload (sanitized)',
          'canonical_payload',
          'meta_json.window_style.templatePayload (post-sanitizeVisualMeta whitelist)',
          'Bay detection, bay schematic slot, tangent width, projection depth, facet widths, joint angles',
          'calculated',
          'Kept by the sanitizeVisualMeta whitelist. root.preset === "bay" marks the item bay-like; presentation.bayConfig and root geometry supply the bay measurements. Not live-rendered',
          'Non-bay behaviour when preset missing',
          'supabase/functions/public-doc/index.ts#visualDesignerItems (isBayLike, bayConfig)',
          { operationPreset: 'canonicalPayloadRead' },
        ),
        sub(
          'public_customer_pdf',
          'preview_path_presentation_external',
          'Presentation outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_presentation_external',
          'Presentation · Outside',
          'exported',
          'Public URL embedded in PDF HTML',
          'Falls back to preview_path / previewPath',
          'supabase/functions/public-doc/index.ts#applyVisualDesignerTemplateBlocks',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'public_customer_pdf',
          'preview_path_design_external',
          'Design outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_external',
          'Design · Outside',
          'exported',
          'Stored PNG in customer PDF tile grid',
          'Omit slot when empty',
          'supabase/functions/public-doc/index.ts#applyVisualDesignerTemplateBlocks',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'public_customer_pdf',
          'logical_divs',
          'Logical division backup',
          'quote_owned',
          'meta_json.specs.options.logical_divs',
          'Not exported to customer PDF',
          'omitted',
          'Not included in sanitizeVisualMeta options whitelist',
          'Omitted',
          'src/modules/docs/utils/publicVisualMeta.ts#sanitizeVisualMeta',
          { operationPreset: 'fieldOmittedSanitize' },
        ),
      ],
    },
    {
      id: 'public_document_viewer',
      name: 'Public document viewer',
      audience: 'customer',
      renderSource: 'stored_png',
      description:
        'Read-only public document viewer for shared quote links (public-doc edge API). Line meta is sanitized via the same whitelist as public customer PDF; getDesignerSummaryRows runs on that filtered shape. Omitted keys documented per-field.',
      entryPoint: 'src/modules/docs/pages/PublicDocViewerPage.tsx',
      subscriptions: [
        ...summarySubscriptionsForArtifact('public_document_viewer'),
        sub(
          'public_document_viewer',
          'preview_path_presentation_external',
          'Presentation outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_presentation_external',
          'Primary thumbnail / hero image',
          'displayed',
          'toPublicImageUrl resolution chain',
          'Falls back through preview_path variants',
          'src/modules/docs/pages/PublicDocViewerPage.tsx',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'public_document_viewer',
          'preview_path_design_external',
          'Design outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_external',
          'Design view tab image',
          'conditional',
          'Shown when design variant selected in viewer',
          'Hidden when URL missing',
          'src/modules/docs/pages/PublicDocViewerPage.tsx',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'public_document_viewer',
          'templatePayload',
          'Window Generator payload',
          'canonical_payload',
          'meta_json.window_style.templatePayload',
          'Bay detection / profile formatting',
          'calculated',
          'isBayLikePayload and formatProfileDefinition read payload shape',
          'Ignored when payload incomplete',
          'src/modules/docs/pages/PublicDocViewerPage.tsx',
          { operationPreset: 'canonicalPayloadRead' },
        ),
      ],
    },
    {
      id: 'document_detail_ui',
      name: 'Document detail UI',
      audience: 'staff',
      renderSource: 'mixed',
      description: 'Staff document detail page fenestration grid and Visual Designer line presentation.',
      entryPoint: 'src/modules/docs/pages/DocumentDetailPage.tsx',
      subscriptions: [
        ...summarySubscriptionsForArtifact('document_detail_ui'),
        sub(
          'document_detail_ui',
          'preview_path_presentation_external',
          'Presentation outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_presentation_external',
          'Line card thumbnail',
          'displayed',
          'Stored PNG preferred; may coexist with inline WG preview in modal',
          'Legacy preview_path fallback',
          'src/modules/docs/pages/DocumentDetailPage.tsx',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'document_detail_ui',
          'templatePayload',
          'Window Generator payload',
          'canonical_payload',
          'meta_json.window_style.templatePayload',
          'Modal edit / export merge source',
          'calculated',
          'buildLatestWindowGeneratorPayloadFromMeta on PDF export path',
          'Null payload skips WG features',
          'src/modules/docs/pages/DocumentDetailPage.tsx#renderTemplate',
          { operationPreset: 'canonicalPayloadRead', relationshipPreset: 'canonicalPayloadReference', domainOwner: 'canonical_window' },
        ),
      ],
    },
    {
      id: 'visual_designer_modal_preview',
      name: 'Visual Designer modal preview',
      audience: 'internal',
      renderSource: 'live_render',
      description:
        'In-wizard live preview while adding or editing a Visual Designer line (step 4) and the Configure window design modal. Uses session workingPayload kept in sync with form fields.',
      entryPoint:
        'QuoteVisualDesignerCard.tsx — workingPayload state; v2_lite useEffect form sync (mergeModalStateIntoPayload + buildModalMergeStateFromForm); payloadToRender preview; WindowGenerator Configure modal',
      subscriptions: [
        sub(
          'visual_designer_modal_preview',
          'working_payload_session',
          'Session workingPayload',
          'derived_cache',
          'React state: workingPayload / workingPayloadRef',
          'Preview tiles, Configure modal, Save, PNG generation',
          'calculated',
          'Single in-memory payload shared across Preview, Configure, Save, and uploadVisualDesignerPreviewsAndGetUrls',
          'Seeded from saved templatePayload or library style on open',
          'QuoteVisualDesignerCard.tsx#workingPayload; visualDesignerPhase1.integration.test.ts',
          {
            operationPreset: 'modalWorkingPayloadSync',
            relationshipPreset: 'modalWorkingSession',
            domainOwner: 'derived_cache',
            currentReadSource: 'workingPayload (in-memory session state)',
          },
        ),
        sub(
          'visual_designer_modal_preview',
          'form_merge_sync',
          'Form → payload merge',
          'derived_cache',
          'mergeModalStateIntoPayload(workingPayload, buildModalMergeStateFromForm())',
          'Keeps workingPayload aligned with modal form fields',
          'calculated',
          'Includes widthMm and heightMm from form state; applied in the v2_lite form-sync useEffect',
          'Skipped when designerVersion !== v2_lite',
          'QuoteVisualDesignerCard.tsx#buildModalMergeStateFromForm; #mergeModalStateIntoPayload',
          {
            operationPreset: 'modalWorkingPayloadSync',
            relationshipPreset: 'modalWorkingSession',
            domainOwner: 'derived_cache',
            currentReadSource: 'Form fields → mergeModalStateIntoPayload',
          },
        ),
        sub(
          'visual_designer_modal_preview',
          'modal_preview_render',
          'Step 4 SVG preview tiles',
          'derived_cache',
          'workingPayload || v2PayloadToUse || style.payload',
          'WindowGeneratorSvg presentation/design tiles',
          'displayed',
          'No separate merge at render time; reads already-synced workingPayload',
          'Falls back to stored PNG URLs when payload incomplete',
          'QuoteVisualDesignerCard.tsx#payloadToRender',
          {
            operationPreset: 'modalPreviewRender',
            relationshipPreset: 'modalWorkingSession',
            domainOwner: 'derived_cache',
            currentReadSource: 'workingPayload || v2PayloadToUse',
          },
        ),
        sub(
          'visual_designer_modal_preview',
          'templatePayload',
          'Saved templatePayload seed',
          'canonical_payload',
          'meta_json.window_style.templatePayload (on edit open) or library style.payload',
          'Initial workingPayload seed',
          'calculated',
          'Restored when opening an existing line for edit; not re-derived from specs on each preview frame',
          'Library template used for new lines',
          'QuoteVisualDesignerCard.tsx#workingPayload seed; openEdit hydration',
          {
            operationPreset: 'canonicalPayloadRead',
            relationshipPreset: 'canonicalPayloadReference',
            domainOwner: 'canonical_window',
          },
        ),
        sub(
          'visual_designer_modal_preview',
          'width_mm',
          'Width (form field)',
          'quote_owned',
          'Form state widthMm → buildModalMergeStateFromForm → mergeModalStateIntoPayload',
          'Merged into workingPayload w/h pixels',
          'calculated',
          'Form dimensions ARE passed to merge (unlike closed-card preview). Same path used for Save PNG (uploadVisualDesignerPreviewsAndGetUrls) and persisted templatePayload',
          'Empty form field preserves payload-derived dimensions',
          'QuoteVisualDesignerCard.tsx#buildModalMergeStateFromForm',
          {
            operationPreset: 'modalWorkingPayloadSync',
            relationshipPreset: 'modalWorkingSession',
            domainOwner: 'canonical_window',
            currentReadSource: 'Form widthMm → mergeModalStateIntoPayload',
          },
        ),
        sub(
          'visual_designer_modal_preview',
          'modal_stored_png_fallback',
          'Stored PNG fallback in wizard',
          'derived_cache',
          'meta_json.window_style.preview_path_* (style option URLs)',
          'Image tiles when live payload unavailable',
          'conditional',
          'Used in step 4 when payloadToRender lacks root/w/h; read-only URL embed',
          'Live workingPayload takes precedence when present',
          'QuoteVisualDesignerCard.tsx#payloadToRender stored PNG fallback',
          { operationPreset: 'storedPngCachedRead', domainOwner: 'derived_cache' },
        ),
      ],
    },
    {
      id: 'quote_line_card_live_preview',
      name: 'Quote-line card live preview',
      audience: 'internal',
      renderSource: 'live_render',
      description:
        'Live SVG previews on saved Visual Designer lines in the fenestration grid. Current v2_lite lines render the accepted saved templatePayload directly; legacy lines use specs reconciliation behind resolveQuoteLineCardLivePayload.',
      entryPoint:
        'QuoteVisualDesignerCard.tsx — resolveQuoteLineCardLivePayload (visualDesignerPayloadResolution.ts)',
      subscriptions: [
        sub(
          'quote_line_card_live_preview',
          'card-live-preview',
          'Compact card live SVG',
          'derived_cache',
          'resolveQuoteLineCardLivePayload(meta, libraryTemplates)',
          'WindowGeneratorSvg tiles on saved quote lines',
          'displayed',
          'Current v2_lite lines: clone saved templatePayload (hasAuthoritativeSavedTemplatePayload). Legacy: specs overlay with blank width/height',
          'Falls back to stored PNG when payload unavailable',
          'visualDesignerPayloadResolution.ts#resolveQuoteLineCardLivePayload; QuoteVisualDesignerCard.tsx',
          {
            operationPreset: 'canonicalPayloadRead',
            relationshipPreset: 'authoritativeSavedPayload',
            domainOwner: 'canonical_window',
            currentReadSource: 'Saved templatePayload (authoritative) or legacy merge',
          },
        ),
        sub(
          'quote_line_card_live_preview',
          'templatePayload',
          'Saved templatePayload',
          'canonical_payload',
          'meta_json.window_style.templatePayload',
          'Authoritative geometry for current v2_lite lines',
          'calculated',
          'Required for hasAuthoritativeSavedTemplatePayload; written on Visual Designer Save',
          'Legacy lines may fall back to library template via resolveCanonicalTemplatePayload',
          'visualDesignerPayloadResolution.ts#hasAuthoritativeSavedTemplatePayload',
          {
            operationPreset: 'canonicalPayloadRead',
            relationshipPreset: 'canonicalPayloadReference',
            domainOwner: 'canonical_window',
          },
        ),
        sub(
          'quote_line_card_live_preview',
          'legacy-specs-reconciliation',
          'Legacy specs reconciliation',
          'derived_cache',
          'mergeModalStateIntoPayload(base, buildLegacyQuoteLineCardMergeState) — legacy path only',
          'Compact card SVG for pre-authoritative lines',
          'conditional',
          'Used when designer_version !== v2_lite or no saved templatePayload on line',
          'Not applied to current v2_lite lines with saved templatePayload',
          'visualDesignerPayloadResolution.ts#buildLegacyQuoteLineCardMergeState',
          {
            operationPreset: 'cardBlankDimensionMerge',
            relationshipPreset: 'cardBlankDimensionVsExport',
            domainOwner: 'derived_cache',
            currentReadSource: 'Library/template base + meta_json.specs overlay',
          },
        ),
      ],
    },
    {
      id: 'quote_line_stored_png_fallback',
      name: 'Quote-line stored PNG fallback',
      audience: 'internal',
      renderSource: 'stored_png',
      description:
        'Read-only stored preview URLs on saved quote lines when live templatePayload merge is unavailable or PNGs were persisted at save time.',
      entryPoint:
        'QuoteVisualDesignerCard.tsx — visualDesignerLines grid PNG branch and wizard payloadToRender PNG fallback',
      subscriptions: [
        sub(
          'quote_line_stored_png_fallback',
          'preview_path_presentation_external',
          'Presentation outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_presentation_external',
          'Closed card presentation · outside tile',
          'displayed',
          'ImageCanvasViewer embed; no merge or regeneration in this path',
          'Used when livePayloadForCard unavailable or wizard payload incomplete',
          'QuoteVisualDesignerCard.tsx#visualDesignerLines grid presentation tile',
          { operationPreset: 'storedPngCachedRead', domainOwner: 'derived_cache' },
        ),
        sub(
          'quote_line_stored_png_fallback',
          'preview_path_design_external',
          'Design outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_external',
          'Closed card design · outside tile',
          'displayed',
          'Read-only URL from PNG generated at last Save or prior export',
          'Omit slot when URL missing',
          'QuoteVisualDesignerCard.tsx#visualDesignerLines grid design tile',
          { operationPreset: 'storedPngCachedRead', domainOwner: 'derived_cache' },
        ),
        sub(
          'quote_line_stored_png_fallback',
          'preview_path_design_internal',
          'Design inside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_internal',
          'Closed card design · inside tile',
          'displayed',
          'Read-only cached derivative; not canonical geometry authority',
          'Omit slot when URL missing',
          'QuoteVisualDesignerCard.tsx',
          { operationPreset: 'storedPngCachedRead', domainOwner: 'derived_cache' },
        ),
      ],
    },
    {
      id: 'supplier_pdf',
      name: 'Supplier PDF',
      audience: 'supplier',
      renderSource: 'mixed',
      description:
        'Dedicated technical PDF attached when sending Visual Designer lines to suppliers. Type, profile, glass, bead, material, colours (split external / internal), handle, hinge, hinge type, open direction, master leaf, panels, stack, traffic door, sliding panels, Georgian bars, trickle vents and cill come from getDesignerSummaryRows (carried on the basket item), so wording and door rules match the Quote page. Reference, size, bay rows, astragal, quantity and notes keep their own capitalised rows. Blank rows are dropped.',
      entryPoint: 'src/modules/docs/components/SendVisualDesignerToSupplierModal.tsx#buildDedicatedSupplierPdfHtml',
      subscriptions: [
        ...summarySubscriptionsForArtifact('supplier_pdf'),
        sub(
          'supplier_pdf',
          'preview_path_design_external',
          'Design outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_external',
          'Primary technical page hero image',
          'exported',
          'Enlarged design-outside view on page 1',
          'Falls back to generic preview_path',
          'SendVisualDesignerToSupplierModal.tsx#buildDedicatedSupplierPdfHtml',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'supplier_pdf',
          'preview_path_presentation_external',
          'Presentation outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_presentation_external',
          'Appendix presentation views',
          'exported',
          'Moved to appendix pages in supplier layout',
          'Omit appendix page when URL missing',
          'SendVisualDesignerToSupplierModal.tsx',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'supplier_pdf',
          'wg-payload-merge',
          'Merged WG payload',
          'derived_cache',
          'buildLatestWindowGeneratorPayloadFromMeta(meta)',
          'Dimension overlay / open-direction labels',
          'calculated',
          'Regenerates or reads from merged payload for technical annotations',
          'Raw templatePayload if merge fails',
          'SendVisualDesignerToSupplierModal.tsx#getWindowGeneratorPayloadFromMeta',
          { operationPreset: 'compatibilityOverlayCalculate', relationshipPreset: 'compatibilityOverlay', domainOwner: 'derived_cache' },
        ),
        ...SUPPLIER_PDF_FIELD_ROWS.map(([fieldKey, subscribedField, sourcePath, label, rule, fallback]) =>
          sub(
            'supplier_pdf',
            fieldKey,
            subscribedField,
            'quote_owned',
            sourcePath,
            label,
            'exported',
            rule,
            fallback,
            'SendVisualDesignerToSupplierModal.tsx#buildDedicatedSupplierPdfHtml (leadingSpecRows / finishingSpecRows / baySizeRows)',
            { operationPreset: 'directReadFormat', relationshipPreset: 'ownFormatterSeparateUpdate', domainOwner: domainOwnerFor(fieldKey) },
          ),
        ),
      ],
    },
    {
      id: 'supplier_email_cid',
      name: 'Supplier email / CID',
      audience: 'supplier',
      renderSource: 'stored_png',
      description:
        'Outlook email body with inline CID images when sending to suppliers via Microsoft Graph. {{frameColorExternal}} / {{frameColorInternal}}, {{hingePlacement}}, {{openDirection}}, {{hingeType}}, {{trickleVents}} and {{cill}} take their text from getDesignerSummaryRows; templates must use the split colour tags to show split colours.',
      entryPoint: 'src/modules/docs/components/SendVisualDesignerToSupplierModal.tsx → graphService.sendTemplateEmail',
      subscriptions: [
        ...summarySubscriptionsForArtifact('supplier_email_cid'),
        sub(
          'supplier_email_cid',
          'preview_path_design_external',
          'Design outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_external',
          'Inline CID image attachment',
          'exported',
          'JPEG-compressed blob attached as cid: contentId in HTML email',
          'Skips line when image fetch/compress fails',
          'SendVisualDesignerToSupplierModal.tsx; src/utils/graphService.ts',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'supplier_email_cid',
          'line_name',
          'Line display name',
          'quote_owned',
          'document_line.name',
          'Email table row title',
          'displayed',
          'Plain text in supplier email summary table',
          'Fallback to window type name',
          'SendVisualDesignerToSupplierModal.tsx',
          { operationPreset: 'directReadFormat' },
        ),
        sub(
          'supplier_email_cid',
          'reference',
          'Reference',
          'quote_owned',
          'meta_json.specs.reference',
          'Email spec snippet',
          'displayed',
          'Direct read — not via getDesignerSummaryRows',
          'Omitted when blank',
          'SendVisualDesignerToSupplierModal.tsx',
          { operationPreset: 'directReadFormat' },
        ),
        ...SUPPLIER_EMAIL_FIELD_ROWS.map(([fieldKey, subscribedField, sourcePath, label, rule, fallback]) =>
          sub(
            'supplier_email_cid',
            fieldKey,
            subscribedField,
            'quote_owned',
            sourcePath,
            label,
            'displayed',
            rule,
            fallback,
            'src/utils/graphService.ts#sendTemplateEmail item variables; src/templates/WindowOrderEmailTemplate.tsx#WINDOW_ITEM_VARIABLES',
            { operationPreset: 'directReadFormat', relationshipPreset: 'ownFormatterSeparateUpdate', domainOwner: domainOwnerFor(fieldKey) },
          ),
        ),
      ],
    },
    {
      id: 'supplier_basket_order',
      name: 'Supplier basket / order projection',
      audience: 'supplier',
      renderSource: 'mixed',
      description:
        'Window basket and checkout projection when Visual Designer lines are added to a supplier order.',
      entryPoint: 'SendVisualDesignerToSupplierModal.tsx#visualDesignerLinesToWindowBasketItems → windowOrder.ts',
      subscriptions: [
        ...summarySubscriptionsForArtifact('supplier_basket_order'),
        sub(
          'supplier_basket_order',
          'window_type_id',
          'Window type',
          'quote_owned',
          'meta_json.window_type.id',
          'WindowBasketItem.windowType',
          'calculated',
          'Maps aluminium/timber → casement, upvc-door → door; other ids (e.g. door-window, french-doors) pass through unchanged, and the door count includes french-doors, bifold-doors and sliding-doors',
          'Defaults to casement',
          'SendVisualDesignerToSupplierModal.tsx#visualDesignerLinesToWindowBasketItems',
          { operationPreset: 'inMemoryCalculate' },
        ),
        sub(
          'supplier_basket_order',
          'width_mm',
          'Width',
          'quote_owned',
          'meta_json.specs.width_mm / bay_tangent_width_mm',
          'WindowBasketItem widthMm',
          'calculated',
          'Bay lines prefer bay tangent width from options or payload geometry — direct basket mapping, not summary rows',
          'Zero when missing',
          'SendVisualDesignerToSupplierModal.tsx#deriveBayMetricsFromPayload',
          { operationPreset: 'inMemoryCalculate' },
        ),
        sub(
          'supplier_basket_order',
          'height_mm',
          'Height',
          'quote_owned',
          'meta_json.specs.height_mm',
          'WindowBasketItem heightMm',
          'exported',
          'Numeric mm from specs — direct basket mapping',
          'Zero when missing',
          'SendVisualDesignerToSupplierModal.tsx',
          { operationPreset: 'inMemoryCalculate' },
        ),
        sub(
          'supplier_basket_order',
          'cill_option',
          'Cill option',
          'quote_owned',
          'meta_json.specs.options.cill_option',
          'cillIncluded / cillHeight / cillType',
          'calculated',
          '150/180/stub mapped to height and Regular/Stub type flags — direct basket mapping, not summary rows',
          'none → not included',
          'SendVisualDesignerToSupplierModal.tsx',
          { operationPreset: 'inMemoryCalculate' },
        ),
        sub(
          'supplier_basket_order',
          'preview_path_design_external',
          'Design outside PNG',
          'derived_cache',
          'meta_json.window_style.preview_path_design_external',
          'imageDesignOutside on basket item',
          'exported',
          'toPublicImageUrl for order confirmation display',
          'Falls back to presentation or legacy preview',
          'SendVisualDesignerToSupplierModal.tsx',
          { operationPreset: 'storedPngCachedRead' },
        ),
        sub(
          'supplier_basket_order',
          'templatePayload',
          'WG payload',
          'canonical_payload',
          'meta_json.window_style.templatePayload',
          'Bay metrics, opens summary, cill placement label',
          'calculated',
          'getWindowGeneratorPayloadFromMeta → deriveBayMetricsFromPayload, deriveOpensSummaryFromPayload',
          'Skipped when payload invalid',
          'SendVisualDesignerToSupplierModal.tsx',
          { operationPreset: 'inMemoryCalculate' },
        ),
        sub(
          'supplier_basket_order',
          'material_name',
          'Material',
          'quote_owned',
          'meta_json.window_material.name / line.materialId lookup',
          'materialName on basket item',
          'calculated',
          'resolveSingleMaterialName prefers explicit material link on line',
          'Empty string when unresolved',
          'SendVisualDesignerToSupplierModal.tsx#resolveSingleMaterialName',
          { operationPreset: 'inMemoryCalculate' },
        ),
        sub(
          'supplier_basket_order',
          'glass_type',
          'Glass type',
          'quote_owned',
          'meta_json.specs.glass_type / glass_type_code',
          'glassType on basket item',
          'calculated',
          'Obscured code maps to Obscured label — direct basket mapping',
          'Defaults to Clear',
          'SendVisualDesignerToSupplierModal.tsx',
          { operationPreset: 'inMemoryCalculate' },
        ),
        ...SUPPLIER_BASKET_EXTRA_ROWS.map(([fieldKey, subscribedField, sourcePath, label, rule, fallback]) =>
          sub(
            'supplier_basket_order',
            fieldKey,
            subscribedField,
            'quote_owned',
            sourcePath,
            label,
            'calculated',
            rule,
            fallback,
            'SendVisualDesignerToSupplierModal.tsx#visualDesignerLinesToWindowBasketItems',
            { operationPreset: 'inMemoryCalculate', domainOwner: domainOwnerFor(fieldKey) },
          ),
        ),
      ],
    },
    {
      id: 'final_survey_review',
      name: 'Final Survey review table',
      audience: 'staff',
      renderSource: 'mixed',
      description:
        'Staff review comparing the First Survey with the Final Survey. Text rows only. extractVisualDesignerFields takes its rows and labels from getDesignerSummaryKeyedRows, plus a Quantity row. Saved Final Survey snapshots keep the review as it was, so past reviews keep their old labels.',
      entryPoint:
        'src/modules/docs/utils/surveyDeltaReview.ts#extractVisualDesignerFields → src/modules/docs/components/FinalSurveyReviewTable.tsx',
      subscriptions: [
        ...summarySubscriptionsForArtifact('final_survey_review'),
        sub(
          'final_survey_review',
          'quantity',
          'Quantity',
          'quote_owned',
          'document_line.qty',
          'Quantity',
          'displayed',
          'Number, inserted after the window type row',
          'Row omitted',
          'src/modules/docs/utils/surveyDeltaReview.ts#extractVisualDesignerFields',
          { operationPreset: 'directReadFormat', relationshipPreset: 'ownFormatterSeparateUpdate', domainOwner: domainOwnerFor('quantity') },
        ),
      ],
    },
    {
      id: 'final_survey_clone',
      name: 'Final Survey line copy',
      audience: 'internal',
      renderSource: 'mixed',
      description:
        'When a Final Survey is started, each Visual Designer line is copied from the First Survey and the surveyor-measured fields are cleared so they must be re-entered.',
      entryPoint: 'src/modules/docs/utils/visualDesignerSurveyClone.ts#cloneLineForFinalSurvey',
      subscriptions: [
        sub(
          'final_survey_clone',
          'final-survey-measure-clear',
          'Surveyor-measured fields',
          'quote_owned',
          'meta_json.specs.width_mm, height_mm; options.cill_*, ext_*_mm, bay_*',
          'Cleared on the Final Survey copy',
          'calculated',
          'clearFinalEditableMeasures sets width, height, cill, extenders and bay measurements to null on the copied line',
          'Other specs are copied unchanged',
          'src/modules/docs/utils/visualDesignerSurveyClone.ts#clearFinalEditableMeasures',
          {
            operationPreset: 'directReadOnly',
            relationshipPreset: 'ownFormatterSeparateUpdate',
            domainOwner: 'quote',
            operationOverride: {
              accessMode: 'derived_calculated',
              operation: 'persist_duplicate',
              additionalPersistedCopy: 'independently_editable_copy',
              riskRationale:
                'Creates an independently editable copy of the line for the Final Survey by design. A new surveyor-measured field must be added to clearFinalEditableMeasures by hand.',
            },
          },
        ),
      ],
    },
    {
      id: 'line_description',
      name: 'Quote line description text',
      audience: 'staff',
      renderSource: 'mixed',
      description:
        'Plain-text description saved on each Visual Designer quote line ("Label: value" parts joined with •). buildVisualDesignerLineDescription takes the parts from getDesignerSummaryKeyedRows using LINE_DESCRIPTION_ROW_KEYS. Only new or re-saved lines get the new text; existing descriptions are not rewritten.',
      entryPoint: 'src/modules/docs/components/QuoteVisualDesignerCard.tsx#buildVisualDesignerLine',
      subscriptions: summarySubscriptionsForArtifact('line_description'),
    },
    {
      id: 'description_template_counts',
      name: 'Description template window/door counts',
      audience: 'staff',
      renderSource: 'mixed',
      description: 'Description Assistant templates count windows and doors across Visual Designer lines to build wording like "3 windows and 1 door".',
      entryPoint: 'src/modules/docs/data/descriptionTemplates.ts → DescriptionAssistant.tsx',
      subscriptions: [
        sub(
          'description_template_counts',
          'window_type_id',
          'Window type',
          'quote_owned',
          'meta_json.window_type.id, window_type.name',
          'Window / door counts in description text',
          'calculated',
          'Shared isDoorType (upvc-door, french-doors, bifold-doors, sliding-doors, or name contains the word "door", so Door & Window counts as a door) → door count, anything else → window count (weighted by qty)',
          'Counted as window',
          'src/modules/docs/data/descriptionTemplates.ts (isDoorType)',
          { operationPreset: 'inMemoryCalculate', relationshipPreset: 'sharedBuilderQuoteField', domainOwner: 'quote' },
        ),
      ],
    },
    {
      id: 'dashboard_fenestration',
      name: 'Dashboard fenestration averages',
      audience: 'staff',
      renderSource: 'mixed',
      description: 'Dashboard statistics split Visual Designer lines into doors and windows and average their sizes.',
      entryPoint: 'src/pages/DashboardPage.tsx (fenestration statistics)',
      subscriptions: [
        sub(
          'dashboard_fenestration',
          'window_type_id',
          'Window type (door test)',
          'quote_owned',
          'meta_json.window_type.id, window_type.name',
          'Door / window mean sizes',
          'calculated',
          'Shared isDoorType (upvc-door, french-doors, bifold-doors, sliding-doors, or name contains the word "door"); a Door & Window line counts as both a door and a window',
          'Counted as window',
          'src/pages/DashboardPage.tsx (isDoorType, typeForMean)',
          { operationPreset: 'inMemoryCalculate', relationshipPreset: 'sharedBuilderQuoteField', domainOwner: 'quote' },
        ),
        sub(
          'dashboard_fenestration',
          'width_height_size',
          'Width and height',
          'quote_owned',
          'meta_json.specs.width_mm, height_mm',
          'Mean width / height',
          'calculated',
          'Sums width and height × qty per door / window group',
          'Lines without both dimensions skipped',
          'src/pages/DashboardPage.tsx',
          { operationPreset: 'inMemoryCalculate', relationshipPreset: 'ownFormatterSeparateUpdate', domainOwner: 'quote' },
        ),
      ],
    },
    {
      id: 'fenestration_sql_tool',
      name: 'Fenestration query tool (database)',
      audience: 'internal',
      renderSource: 'mixed',
      description:
        'Database function behind the fenestration search tool. Derives is_door, opener count and search text from line meta_json inside SQL.',
      entryPoint: 'supabase/migrations/20260810170000-create-fenestration-query-tool.sql',
      subscriptions: [
        sub(
          'fenestration_sql_tool',
          'window_type_id',
          'Window type (door test)',
          'quote_owned',
          'meta_json.window_type.id, window_type.name',
          'is_door column',
          'calculated',
          'is_door when id is upvc-door, french-doors, bifold-doors or sliding-doors, or id+name contains the word "door" (regex)',
          'false',
          'supabase/migrations/20260929120000-fenestration-new-door-types.sql (is_door)',
          { operationPreset: 'inMemoryCalculate', relationshipPreset: 'ownFormatterSeparateUpdate', domainOwner: 'quote' },
        ),
        sub(
          'fenestration_sql_tool',
          'templatePayload',
          'Window Generator payload',
          'canonical_payload',
          'meta_json.window_style.templatePayload.opens',
          'opener_count column',
          'calculated',
          'Counts keys of templatePayload.opens',
          '0 when missing',
          'supabase/migrations/20260810170000-create-fenestration-query-tool.sql (opener_count)',
          { operationPreset: 'canonicalPayloadRead' },
        ),
      ],
    },
  ],
};

/** Re-export for tests and tooling. */
export { GET_DESIGNER_SUMMARY_ROWS_REF };
